<template>
    <Modal
        :open="open"
        :title="title"
        :width="width"
        :confirm-loading="confirmLoading"
        @update:open="$emit('update:open', $event)"
        @ok="handleOk"
        @cancel="handleCancel"
    >
        <a-spin :spinning="confirmLoading" tip="處理中...">
            <div class="edit-content">
                <!-- 表單內容插槽 -->
                <slot name="form"></slot>

                <!-- 預設表單結構 -->
                <a-form
                    v-if="!$slots.form"
                    ref="formRef"
                    :model="formData"
                    :rules="rules"
                    :label-col="{ span: 6 }"
                    :wrapper-col="{ span: 18 }"
                    class="vaporwave-form"
                >
                    <slot name="fields"></slot>
                </a-form>
            </div>
        </a-spin>
    </Modal>
</template>

<script setup>
import { ref } from 'vue';
import Modal from './Modal.vue';

// Props
const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: '編輯'
    },
    width: {
        type: [String, Number],
        default: '600px'
    },
    confirmLoading: {
        type: Boolean,
        default: false
    },
    formData: {
        type: Object,
        default: () => ({})
    },
    rules: {
        type: Object,
        default: () => ({})
    }
});

// Emits
const emit = defineEmits(['update:open', 'ok', 'cancel']);

// Refs
const formRef = ref(null);

// Methods
const handleOk = async () => {
    if (formRef.value) {
        try {
            await formRef.value.validate();
            emit('ok');
        } catch (error) {
            console.error('表單驗證失敗:', error);
        }
    } else {
        emit('ok');
    }
};

const handleCancel = () => {
    emit('cancel');
};
</script>

<style scoped>
.edit-content {
    color: white;
    font-family: 'Courier New', monospace;
}

/* 表單樣式 */
.vaporwave-form :deep(.ant-form-item-label) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
}

.vaporwave-form :deep(.ant-form-item-label > label) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
}

.vaporwave-form
    :deep(.ant-form-item-label > label.ant-form-item-required::before) {
    color: #ff4d4f !important;
}

/* Input 樣式 */
.vaporwave-form :deep(.ant-input) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    color: white !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
}

/* 密碼輸入框樣式 - 修正雙層框框 */
.vaporwave-form :deep(.ant-input-password) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
}

.vaporwave-form :deep(.ant-input-password .ant-input) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: white !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-input-password .ant-input:focus) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
}

.vaporwave-form :deep(.ant-input-password .ant-input-suffix) {
    color: rgba(168, 255, 246, 0.8) !important;
}

.vaporwave-form :deep(.ant-input-password .ant-input-suffix:hover) {
    color: #0e7490 !important;
}

.vaporwave-form :deep(.ant-input-password:hover) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-form :deep(.ant-input-password:focus-within) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
}

.vaporwave-form :deep(.ant-input:hover) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-form :deep(.ant-input:focus) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
    background: rgba(255, 255, 255, 0.9) !important;
}

.vaporwave-form :deep(.ant-input::placeholder) {
    color: rgba(26, 26, 42, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

/* 強制所有 input 文字為墨色 */
.vaporwave-form :deep(.ant-input),
.vaporwave-form :deep(.ant-input *),
.vaporwave-form :deep(.ant-input * *) {
    color: #1a1a2a !important;
}

.vaporwave-form :deep(.ant-input-password .ant-input),
.vaporwave-form :deep(.ant-input-password .ant-input *),
.vaporwave-form :deep(.ant-input-password .ant-input * *) {
    color: #1a1a2a !important;
}

/* Select 樣式 */
.vaporwave-form :deep(.ant-select) {
    background: rgba(255, 255, 255, 0.8) !important;
}

.vaporwave-form :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(168, 255, 246, 0.5) !important;
    border-radius: 8px !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-select-selector:hover) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-form :deep(.ant-select-focused .ant-select-selector) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
}

.vaporwave-form :deep(.ant-select-selection-item) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-select-selection-placeholder) {
    color: rgba(26, 26, 42, 0.6) !important;
    font-family: 'Courier New', monospace !important;
}

/* Select Dropdown 樣式 */
.vaporwave-form :deep(.ant-select-dropdown) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 2px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 12px !important;
    box-shadow:
        0 0 20px rgba(255, 158, 207, 0.3),
        0 0 40px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-form :deep(.ant-select-item) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
    margin: 4px 8px !important;
}

.vaporwave-form :deep(.ant-select-item:hover) {
    background: rgba(168, 255, 246, 0.2) !important;
    color: #1a1a2a !important;
}

.vaporwave-form :deep(.ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.3) !important;
    color: #1a1a2a !important;
}

/* Textarea 樣式 */
.vaporwave-form :deep(.ant-input[type='textarea']) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(168, 255, 246, 0.5) !important;
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
    border-radius: 8px !important;
    resize: vertical !important;
}

.vaporwave-form :deep(.ant-input[type='textarea']:hover) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.2) !important;
}

.vaporwave-form :deep(.ant-input[type='textarea']:focus) {
    border-color: rgba(200, 200, 210, 0.4) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3) !important;
    background: rgba(255, 255, 255, 0.9) !important;
}

/* Radio 樣式 */
.vaporwave-form :deep(.ant-radio-wrapper) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-radio-inner) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(42, 74, 122, 0.5) !important;
}

.vaporwave-form :deep(.ant-radio-inner:hover) {
    border-color: #2a4a7a !important;
}

.vaporwave-form :deep(.ant-radio-checked .ant-radio-inner) {
    background: #2a4a7a !important;
    border-color: #2a4a7a !important;
}

.vaporwave-form :deep(.ant-radio-checked .ant-radio-inner::after) {
    background: white !important;
}

/* Checkbox 樣式 */
.vaporwave-form :deep(.ant-checkbox-wrapper) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-checkbox-inner) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(42, 74, 122, 0.5) !important;
}

.vaporwave-form :deep(.ant-checkbox-inner:hover) {
    border-color: #2a4a7a !important;
}

.vaporwave-form :deep(.ant-checkbox-checked .ant-checkbox-inner) {
    background: #2a4a7a !important;
    border-color: #2a4a7a !important;
}

.vaporwave-form :deep(.ant-checkbox-checked .ant-checkbox-inner::after) {
    border-color: white !important;
}

/* Switch 開關樣式 */
.vaporwave-form :deep(.ant-switch) {
    background-color: rgba(42, 74, 122, 0.3) !important;
    border: 1px solid rgba(42, 74, 122, 0.5) !important;
}

.vaporwave-form :deep(.ant-switch-checked) {
    background-color: #2a4a7a !important;
    border-color: #2a4a7a !important;
}

.vaporwave-form :deep(.ant-switch-handle) {
    background-color: white !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
}

.vaporwave-form :deep(.ant-switch-checked .ant-switch-handle) {
    background-color: white !important;
    border-color: #2a4a7a !important;
}

.vaporwave-form :deep(.ant-switch-inner) {
    color: #1a1a2a !important;
    font-family: 'Courier New', monospace !important;
}

.vaporwave-form :deep(.ant-switch-checked .ant-switch-inner) {
    color: white !important;
}

/* 表單驗證錯誤樣式 */
.vaporwave-form :deep(.ant-form-item-explain-error) {
    color: #ff4d4f !important;
    font-family: 'Courier New', monospace !important;
    font-size: 12px !important;
}

.vaporwave-form :deep(.ant-form-item-has-error .ant-input) {
    border-color: #ff4d4f !important;
}

.vaporwave-form :deep(.ant-form-item-has-error .ant-input:hover) {
    border-color: #ff4d4f !important;
    box-shadow: 0 0 10px rgba(255, 77, 79, 0.3) !important;
}

.vaporwave-form :deep(.ant-form-item-has-error .ant-input:focus) {
    border-color: #ff4d4f !important;
    box-shadow: 0 0 15px rgba(255, 77, 79, 0.4) !important;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .vaporwave-form :deep(.ant-form-item-label) {
        text-align: left !important;
    }

    .vaporwave-form :deep(.ant-col-6) {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }

    .vaporwave-form :deep(.ant-col-18) {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }
}
</style>
